<template>
  <div class="unified-pay-root">
    <div
      id="payLoadingMask"
      ref="payLoadingMask"
      class="pay-loading-mask"
      :class="{ show: payLoadingVisible }"
      aria-live="polite"
      aria-busy="true"
    >
      <div class="pay-loading-card">
        <p class="pay-loading-title">{{ payLoadingTitle }}</p>
        <p class="pay-loading-sub">{{ payLoadingSub }}</p>
        <div class="pay-loading-bar" role="progressbar">
          <div class="pay-loading-bar-inner"></div>
        </div>
        <p
          v-show="!payLoadingOauthMode"
          class="pay-loading-pct"
          ref="payLoadingHint"
        >{{ payLoadingHint }}</p>
      </div>
    </div>

    <div v-if="pageBooting" class="page">
      <div class="brand">收银台</div>
      <div class="card page-boot-card">
        <p class="pay-loading-title">{{ pageBootTitle }}</p>
        <p class="pay-loading-sub">{{ pageBootSub }}</p>
        <div class="pay-loading-bar" role="progressbar" aria-hidden="true">
          <div class="pay-loading-bar-inner"></div>
        </div>
        <p class="hint page-boot-hint">{{ pageBootHint }}</p>
      </div>
    </div>

    <div v-show="!pageBooting" class="page">
      <div class="brand">收银台</div>
      <div
        class="card"
        ref="payCard"
        :class="{ dim: cardDim }"
      >
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
        </div>

        <section
          v-if="countdownVisible"
          class="countdown-row"
          aria-live="polite"
        >
          <span class="countdown-label">订单剩余有效时间</span>
          <span class="countdown-val">
            <span class="cd-cell">{{ cdHours }}</span>
            <span class="cd-sep">:</span>
            <span class="cd-cell">{{ cdMinutes }}</span>
            <span class="cd-sep">:</span>
            <span class="cd-cell">{{ cdSeconds }}</span>
          </span>
        </section>

        <p v-if="invalidToken" class="hint">链接无效或已失效</p>
        <p
          v-if="!invalidToken"
          class="hint"
          ref="hintExpired"
          :style="expired ? {} : { display: 'none' }"
        >订单已超时，请重新下单</p>

        <p
          v-if="oauthAwaitingPayment"
          class="hint oauth-done-hint"
        >授权已完成，请在转账页完成付款。勿返回本页重复授权；若已关闭支付页请重新下单。</p>

        <div
          v-if="showOauthWaitTips"
          class="oauth-wait-tips"
        >
          <template v-if="isHldChannel && hldResumeAvailable">
            <p class="oauth-wait-tip">第二步：继续完成支付环境准备</p>
            <div class="oauth-wait-notice">
              <p class="oauth-wait-notice-lead">小程序关掉后若回不到暖页<br><em>可回到本页点继续支付</em></p>
              <p class="oauth-wait-notice-sub">将自动打开环境页；关掉小程序后自动出码</p>
            </div>
          </template>
          <template v-else-if="isHldChannel">
            <p class="oauth-wait-tip">第一步：点击「立即支付」完成授权</p>
            <div class="oauth-wait-notice">
              <p class="oauth-wait-notice-lead">若弹出总店小程序<br><em>直接点右上角 ✕ 关闭</em></p>
              <p class="oauth-wait-notice-sub">里面无需任何操作，关掉后将自动继续支付</p>
            </div>
          </template>
          <template v-else>
            <p class="oauth-wait-tip">若提示即将离开，请点击「继续访问」</p>
            <p class="oauth-wait-tip">请耐心等待，成功出码</p>
          </template>
        </div>

        <div
          v-if="showCcbBrand"
          class="ccb-brand"
          aria-label="中国建设银行"
        >
          <div class="ccb-brand-mark" aria-hidden="true"></div>
          <div class="ccb-brand-text">
            <div class="ccb-brand-cn">中国建设银行</div>
            <div class="ccb-brand-en">China Construction Bank</div>
          </div>
        </div>

        <div
          v-if="showCopyOpenPanel"
          class="copy-open-panel"
        >
          <p class="copy-open-hint">提示：外链无法直接跳转，请复制链接后在支付应用内打开</p>
          <p class="copy-open-hint-sub">{{ copyOpenHint }}</p>
          <div class="copy-open-actions">
            <button
              type="button"
              class="btn-copy-link"
              :disabled="expired"
              @click="onCopyPayLink"
            >复制支付链接</button>
          </div>
          <p v-if="copyToast" class="copy-toast">{{ copyToast }}</p>
        </div>

        <div
          v-if="showQrArea"
          class="qr-wrap"
          :class="{ 'qr-wrap--dense': qrIsDense }"
          ref="qrBox"
        >
          <p
            v-if="!invalidToken && !expired"
            class="qr-guide"
            ref="qrGuide"
          >{{ qrGuideText }}</p>
          <div class="qr-shell" :class="{ 'qr-shell--dense': qrIsDense }">
            <img
              v-if="qrImgSrc"
              class="qr-img"
              :class="{ 'qr-rendered': true, 'qr-rendered--dense': qrIsDense }"
              :src="qrImgSrc"
              :style="qrImgStyle"
              alt="支付码"
            />
            <div
              v-show="qrShowCenterLogo && qrImgSrc"
              class="qr-center-logo"
              aria-hidden="true"
            ></div>
            <div
              v-if="!qrImgSrc && !invalidToken && !expired"
              class="qr-placeholder"
            >{{ qrPlaceholderText }}</div>
          </div>
          <div
            v-if="!invalidToken"
            class="stamp"
            ref="stampOverlay"
            :style="expired ? {} : { display: 'none' }"
          >
            <span>已过期</span>
          </div>
        </div>

        <div
          v-if="showImmediatePayButton && !expired && !invalidToken && !oauthAwaitingPayment"
          class="actions"
          ref="payActions"
        >
          <button
            type="button"
            class="btn-pay"
            ref="btnPay"
            :disabled="btnPayDisabled"
            @click="onPayClick"
          >{{ hldResumeAvailable ? '继续支付' : '立即支付' }}</button>
        </div>

        <p
          v-show="errVisible"
          class="err-text"
          ref="errBox"
        >{{ errText }}</p>

        <div v-if="!invalidToken && !isCcbPayPresentation" class="meta">
          <div class="meta-row">
            <span class="meta-label">金额</span>
            <span class="meta-val">{{ amountYuan }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">商户订单</span>
            <span class="meta-val">{{ mchOrderNo || '-' }}</span>
          </div>
          <div class="meta-row">
            <span class="meta-label">创建时间</span>
            <span class="meta-val">{{ createTime || '-' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="showPayerNameModal"
      class="payer-name-mask"
      role="dialog"
      aria-modal="true"
      aria-labelledby="payerNameTitle"
    >
      <div class="payer-name-dialog">
        <h3 id="payerNameTitle" class="payer-name-title">提示</h3>
        <p class="payer-name-text">请输入付款人姓名（用于确认订单）</p>
        <input
          v-model="payerNameInput"
          class="payer-name-input"
          type="text"
          maxlength="64"
          placeholder="点这里输入付款人姓名"
          @keyup.enter="onConfirmPayerName"
        />
        <button
          type="button"
          class="btn-pay payer-name-confirm"
          :disabled="payerNameSubmitting"
          @click="onConfirmPayerName"
        >确定</button>
        <p v-if="payerNameError" class="err-text">{{ payerNameError }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import { fetchUnifiedCashierPage, submitUnifiedPayerName } from '@/api/api'
import {
  applyExpireUi,
  applyImmediatePay,
  resolveDouyinzfAndroidBridgeLaunchUrl,
  resolveDouyinzfAndroidQrUrl,
  openPayNavigationUrl,
  createPayLoadingController,
  loadPayloadForPay,
  requestExecutePayload,
  isExecutePayloadReady,
  resolveQrDisplay,
  shouldDismissPayLoadingAfterImmediatePay,
  buyerOAuthQrFallback,
  runBuyerOAuthForImmediatePay,
  resolveOAuthProcessLoading,
  shouldPreferInAppOAuthLaunch,
  isJsapiConvertMode,
  runJsapiImmediatePay,
  setupJsapiConvertPoll,
  isDesktopBrowser,
  hasAlipayJsRuntime,
  isAlipayWebView,
  resolveCcbPayPresentation,
  pickQrPresentationUrl,
  redirectCashierExpiredPay,
  handleExecuteExpiredPayload,
  isUnsupportedSinopecUnionPay,
  UNSUPPORTED_SINOPEC_UNION_PAY_MSG,
  openHttpsInAlipayBridge
} from '@/utils/unifiedCashierCore'
import { buildQrImageSrc, computeQrPixelSize, ensureQrLibLoaded } from '@/utils/qrRender'
import {
  isCopyOpenPresentation,
  isPayerNameThenPayPresentation,
  resolveCopyOpenUrl,
  copyTextToClipboard,
  DEFAULT_COPY_OPEN_HINT
} from '@/utils/copyOpenCashier'

const UNIFIED_PAY_ROOT_FONT = '16px'
const UNIFIED_PAY_STYLE_ID = 'unified-pay-stylesheet'

export default {
  name: 'UnifiedPay',
  data () {
    return {
      orderId: '',
      token: '',
      pageBooting: true,
      pageBootHint: '正在生成支付二维码…',
      pageBootTitle: '正在为您分配收款账号',
      pageBootSub: '请稍候，正在连接支付渠道…',
      invalidToken: false,
      expired: false,
      showQrCode: true,
      deferUpstreamPay: false,
      douyinzfChannel: false,
      douyinzfAndroidQrFallback: false,
      qrAlipayImmediateLaunchMode: 'yuansheng',
      expireAtMillis: null,
      amountYuan: '--',
      mchOrderNo: '',
      createTime: '',
      payType: '',
      latestPayload: null,
      preloadPromise: null,
      payLoadingVisible: false,
      payLoadingOauthMode: false,
      payLoadingTitle: '正在为您分配收款账号',
      payLoadingSub: '请稍候，正在连接支付渠道…',
      payLoadingHint: '处理中',
      payLoadingCtrl: null,
      remainSec: 0,
      countdownTimer: null,
      errVisible: false,
      errText: '',
      btnPayDisabled: true,
      qrPlaceholderText: '正在生成可扫码链接…',
      qrImgSrc: '',
      qrShowCenterLogo: false,
      qrIsDense: false,
      qrEdgePx: computeQrPixelSize(),
      cardDim: false,
      buyerOAuthRequired: false,
      buyerOAuthOpenUrl: '',
      buyerOAuthInAppLaunchUrl: '',
      buyerOAuthExternalLaunchScheme: '',
      buyerOAuthPullIsvAppId: '',
      buyerOAuthProcessUrl: '',
      buyerOAuthStateConsumed: false,
      orderPaying: false,
      channelId: null,
      channelName: '',
      hldChannel: false,
      hldResumeWarmUrl: '',
      jsapiRequired: false,
      jsapiMpLaunchScheme: '',
      jsapiMpLaunchRenderUrl: '',
      jsPayMode: 'TRADE_PAY',
      jsapiConvertPollTeardown: null,
      cashierPresentation: 'STANDARD',
      copyOpenHint: DEFAULT_COPY_OPEN_HINT,
      copyOpenPayUrl: '',
      copyToast: '',
      payerNameFilled: false,
      payerNameInput: '',
      payerNameSubmitting: false,
      payerNameError: ''
    }
  },
  computed: {
    buyerOAuthContext () {
      return {
        buyerOAuthRequired: this.buyerOAuthRequired,
        buyerOAuthOpenUrl: this.buyerOAuthOpenUrl,
        buyerOAuthInAppLaunchUrl: this.buyerOAuthInAppLaunchUrl,
        buyerOAuthExternalLaunchScheme: this.buyerOAuthExternalLaunchScheme,
        buyerOAuthPullIsvAppId: this.buyerOAuthPullIsvAppId,
        buyerOAuthProcessUrl: this.buyerOAuthProcessUrl,
        token: this.token,
        buyerOAuthStateConsumed: this.buyerOAuthStateConsumed,
        orderPaying: this.orderPaying,
        channelId: this.channelId,
        channelName: this.channelName,
        hldChannel: this.hldChannel,
        hldResumeWarmUrl: this.hldResumeWarmUrl
      }
    },
    hldResumeAvailable () {
      return !!(String(this.hldResumeWarmUrl || '').trim())
    },
    isHldChannel () {
      if (this.hldChannel) {
        return true
      }
      const id = this.channelId != null ? String(this.channelId) : ''
      const name = String(this.channelName || '').toLowerCase()
      return id === '10005' || name.includes('hld') || name.includes('3vast') || name.includes('慧徕')
    },
    buyerOAuthQrFallbackText () {
      return buyerOAuthQrFallback(this.buyerOAuthContext)
    },
    /** 有 getAuthCode 桥接时走 OAuth 编排，勿再 execute 打开 openauth */
    preferInAppOAuthPay () {
      return shouldPreferInAppOAuthLaunch(this.buyerOAuthContext)
    },
    preferJsapiPay () {
      return !this.invalidToken && !this.expired && this.jsapiRequired && !this.orderPaying
    },
    jsapiContext () {
      return {
        jsapiRequired: this.jsapiRequired,
        jsapiMpLaunchScheme: this.jsapiMpLaunchScheme,
        jsapiMpLaunchRenderUrl: this.jsapiMpLaunchRenderUrl,
        jsPayMode: this.jsPayMode,
        token: this.token
      }
    },
    oauthAwaitingPayment () {
      return !this.invalidToken
        && !this.expired
        && this.buyerOAuthRequired
        && (this.buyerOAuthStateConsumed || this.orderPaying)
    },
    showOauthWaitTips () {
      return !this.invalidToken
        && !this.expired
        && this.buyerOAuthRequired
        && !this.buyerOAuthStateConsumed
        && !this.orderPaying
    },
    /** PC 仅扫码；LKL deferUpstreamPay：双端按钮；抖音直付等同默认：仅手机按钮 */
    showImmediatePayButton () {
      if (this.isCopyOpenMode) {
        return false
      }
      if (this.isPayerNameMode && !this.payerNameFilled) {
        return false
      }
      if (this.deferUpstreamPay) {
        return true
      }
      return !isDesktopBrowser()
    },
    isCopyOpenMode () {
      return isCopyOpenPresentation(this.cashierPresentation)
    },
    isPayerNameMode () {
      return isPayerNameThenPayPresentation(this.cashierPresentation)
    },
    showPayerNameModal () {
      return this.isPayerNameMode
        && !this.pageBooting
        && !this.invalidToken
        && !this.expired
        && !this.payerNameFilled
    },
    showCopyOpenPanel () {
      return this.isCopyOpenMode
        && !this.invalidToken
        && !this.oauthAwaitingPayment
    },
    isCcbPayPresentation () {
      return resolveCcbPayPresentation(this.payType, this.latestPayload)
    },
    showCcbBrand () {
      return this.isCcbPayPresentation
        && !this.invalidToken
        && !this.expired
        && !this.oauthAwaitingPayment
    },
    showQrArea () {
      if (this.isPayerNameMode && !this.payerNameFilled) {
        return false
      }
      // 抖音直付：PC 展码；苹果不展码；安卓仅在一点击之后出兜底码
      if (this.douyinzfChannel && !isDesktopBrowser()) {
        return !!this.douyinzfAndroidQrFallback
      }
      return this.showQrCode
        && !this.oauthAwaitingPayment
        && !this.isCopyOpenMode
    },
    /** 手机抖音直付：进页不预拉，点按钮再插件出链 */
    douyinzfMobileDeferOutcode () {
      return !!this.douyinzfChannel && !isDesktopBrowser()
    },
    /** 插件出链缓冲文案（直冲可能数秒） */
    douyinzfOutcodeLoading () {
      return {
        title: '正在获取支付链接',
        sub: '插件出链中，请稍候勿关闭…',
        hint: '正在连接收款设备…',
        safetyMs: 90000
      }
    },
    qrGuideText () {
      if (this.douyinzfAndroidQrFallback) {
        return '如果手机浏览器跳转支付宝失败，请截图扫码'
      }
      if (this.isCcbPayPresentation) {
        return this.showImmediatePayButton
          ? '请使用建行龙支付 App 扫码，或点击下方「立即支付」唤起 App'
          : '请使用建行龙支付 App 直接扫描上方二维码付款'
      }
      return '打开「扫一扫」'
    },
    countdownVisible () {
      return !this.invalidToken && !this.expired && this.expireAtMillis != null
    },
    cdHours () {
      return this.formatCdCell(Math.floor(this.remainSec / 3600))
    },
    cdMinutes () {
      return this.formatCdCell(Math.floor((this.remainSec % 3600) / 60))
    },
    cdSeconds () {
      return this.formatCdCell(this.remainSec % 60)
    },
    qrImgStyle () {
      return {
        width: `${this.qrEdgePx}px`,
        maxWidth: '100%'
      }
    }
  },
  mounted () {
    document.title = '收银台'
    this._savedHtmlFontSize = document.documentElement.style.fontSize
    document.documentElement.style.fontSize = UNIFIED_PAY_ROOT_FONT
    this.orderId = String(this.$route.params.orderId || '')
    this.token = String(this.$route.query.t || this.$route.query.token || '')
    const vm = this
    this.payLoadingCtrl = createPayLoadingController({
      setVisible (v) { vm.payLoadingVisible = v },
      setOauthMode (v) { vm.payLoadingOauthMode = !!v },
      setTitle (text) { vm.payLoadingTitle = text },
      setSub (text) { vm.payLoadingSub = text },
      setHint (text) { vm.payLoadingHint = text }
    })
    Promise.all([this.injectUnifiedPayStyles(), ensureQrLibLoaded()])
      .then(() => {
        vm.initPage()
      })
      .catch(() => {
        vm.initPage()
      })
  },
  beforeDestroy () {
    this.stopExpireCountdown()
    if (this.jsapiConvertPollTeardown) {
      this.jsapiConvertPollTeardown()
      this.jsapiConvertPollTeardown = null
    }
    if (this.payLoadingCtrl) {
      this.payLoadingCtrl.hide()
    }
    document.body.style.overflow = ''
    if (this._savedHtmlFontSize !== undefined) {
      document.documentElement.style.fontSize = this._savedHtmlFontSize
    }
    this.removeUnifiedPayStyles()
  },
  methods: {
    injectUnifiedPayStyles () {
      if (document.getElementById(UNIFIED_PAY_STYLE_ID)) {
        return Promise.resolve()
      }
      return new Promise((resolve, reject) => {
        const link = document.createElement('link')
        link.id = UNIFIED_PAY_STYLE_ID
        link.rel = 'stylesheet'
        const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
        link.href = `${base}css/unified-pay.css`
        link.onload = () => resolve()
        link.onerror = () => reject(new Error('stylesheet load failed'))
        document.head.appendChild(link)
      })
    },
    removeUnifiedPayStyles () {
      const el = document.getElementById(UNIFIED_PAY_STYLE_ID)
      if (el && el.parentNode) {
        el.parentNode.removeChild(el)
      }
    },
    armDouyinzfAndroidQr (url) {
      const text = String(url || '').trim()
      if (!text || text.toLowerCase().indexOf('douyinzf-alipay-bridge') < 0) {
        return
      }
      this.douyinzfAndroidQrFallback = true
      try {
        sessionStorage.setItem('dyzfAndroidQr:' + this.orderId, text)
      } catch (e) { /* ignore */ }
      this.qrEdgePx = computeQrPixelSize(text)
      buildQrImageSrc(text, { pixelSize: this.qrEdgePx }).then((src) => {
        if (!src || !this.douyinzfAndroidQrFallback) {
          return
        }
        this.qrImgSrc = src
        this.qrShowCenterLogo = false
        this.qrIsDense = false
      })
    },
    restoreDouyinzfAndroidQr () {
      if (!this.douyinzfChannel || isDesktopBrowser() || isAlipayWebView()) {
        return
      }
      if (!/android/i.test(navigator.userAgent || '')) {
        return
      }
      let saved = ''
      try {
        saved = sessionStorage.getItem('dyzfAndroidQr:' + this.orderId) || ''
      } catch (e) {
        saved = ''
      }
      if (saved) {
        this.armDouyinzfAndroidQr(saved)
      }
    },
    renderQrWithFallback (payload) {
      if (this.qrImgSrc) {
        return Promise.resolve(true)
      }
      this.qrEdgePx = computeQrPixelSize(payload && pickQrPresentationUrl(payload))
      return resolveQrDisplay(payload, this.orderId, this.token, this.buyerOAuthQrFallbackText, this.payType).then((result) => {
        if (result && result.src) {
          this.qrImgSrc = result.src
          this.qrShowCenterLogo = !!result.showCenterLogo
          this.qrIsDense = !!result.dense
          if (result.displayEdge) {
            this.qrEdgePx = result.displayEdge
          }
          return true
        }
        this.qrPlaceholderText = this.isCcbPayPresentation
          ? '二维码加载失败，请刷新页面重试'
          : '二维码加载失败，请点「立即支付」'
        return false
      })
    },
    domRefs () {
      const vm = this
      return {
        payCard: this.$refs.payCard,
        hintExpired: this.$refs.hintExpired,
        stampOverlay: this.$refs.stampOverlay,
        payActions: this.$refs.payActions,
        btn: this.$refs.btnPay,
        setQrImg (src, showLogo) {
          vm.qrImgSrc = src
          vm.qrShowCenterLogo = !!showLogo
        }
      }
    },
    showErr (msg) {
      this.errVisible = true
      this.errText = msg || '请求失败'
    },
    hideErr () {
      this.errVisible = false
      this.errText = ''
    },
    formatCdCell (n) {
      const v = Math.max(0, Math.floor(n))
      return String(v).padStart(2, '0')
    },
    stopExpireCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    startExpireCountdown () {
      this.stopExpireCountdown()
      if (this.expired || this.invalidToken || this.expireAtMillis == null) {
        this.remainSec = 0
        return
      }
      const tick = () => {
        const remainMs = this.expireAtMillis - Date.now()
        if (remainMs <= 0) {
          this.remainSec = 0
          this.markExpiredLocal()
          this.stopExpireCountdown()
          return
        }
        this.remainSec = Math.floor(remainMs / 1000)
      }
      tick()
      this.countdownTimer = setInterval(tick, 1000)
    },
    markExpiredLocal () {
      this.expired = true
      this.cardDim = true
      applyExpireUi(this.domRefs())
    },
    finishPageBoot (failed) {
      this.pageBooting = false
      if (failed) {
        this.invalidToken = true
        this.expired = true
        this.amountYuan = '--'
        this.cardDim = true
      }
    },
    /** 扫码 URL 带 autopay=1：进页后尽快自动点「立即支付」（与模板收银台对齐）；可恢复态走暖页恢复。 */
    tryAutopayFromQuery () {
      try {
        if (this.hldResumeAvailable) {
          this.tryHldResumeAuto()
          return
        }
        const q = this.$route && this.$route.query
        const flag = q && String(q.autopay) === '1'
        if (!flag) {
          return
        }
        if (this.invalidToken || this.expired || this.oauthAwaitingPayment) {
          return
        }
        if (!this.preferInAppOAuthPay || this.btnPayDisabled) {
          return
        }
        try {
          const autoKey = 'oauthAutopayOnce:' + String(this.orderId || '') + ':' + String(this.token || '').slice(0, 16)
          if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(autoKey) === '1') {
            return
          }
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(autoKey, '1')
          }
        } catch (e) {
          // ignore
        }
        const delay = (isAlipayWebView() || hasAlipayJsRuntime()) ? 50 : 120
        setTimeout(() => {
          if (this.oauthAwaitingPayment || this.btnPayDisabled) {
            return
          }
          this.onPayClick()
        }, delay)
      } catch (e) {
        // ignore
      }
    },
    hldResumeAutoKey () {
      return 'hldResumeAuto:' + String(this.orderId || '') + ':' + String(this.token || '').slice(0, 8)
    },
    tryHldResumeAuto () {
      if (!this.hldResumeAvailable || this.invalidToken || this.expired || this.oauthAwaitingPayment) {
        return
      }
      try {
        if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(this.hldResumeAutoKey()) === '1') {
          return
        }
      } catch (e) {
        // ignore
      }
      const delay = (isAlipayWebView() || hasAlipayJsRuntime()) ? 80 : 160
      setTimeout(() => {
        this.openHldResumeWarm(true)
      }, delay)
    },
    openHldResumeWarm (fromAuto) {
      const target = String(this.hldResumeWarmUrl || '').trim()
      if (!target) {
        return Promise.reject(new Error('恢复地址未配置'))
      }
      if (fromAuto) {
        try {
          sessionStorage.setItem(this.hldResumeAutoKey(), '1')
        } catch (e) {
          // ignore
        }
      }
      this.hideErr()
      this.btnPayDisabled = true
      this.payLoadingCtrl.show({
        mode: 'oauth',
        title: '正在恢复支付环境',
        sub: '请稍候，即将打开环境页…',
        safetyMs: 60000,
        onSafetyTimeout: () => {
          this.payLoadingCtrl.hide()
          this.showErr('打开超时，请点击「继续支付」重试')
          this.btnPayDisabled = false
        }
      })
      return openHttpsInAlipayBridge(target).catch((e) => {
        this.payLoadingCtrl.hide()
        this.showErr((e && e.message) ? e.message : '打开失败，请重试')
        this.btnPayDisabled = false
        throw e
      })
    },
    applyPageContext (ctx) {
      if (!ctx) {
        this.invalidToken = true
        this.expired = true
        return
      }
      this.invalidToken = !!ctx.invalidToken
      this.expired = !!ctx.expired
      this.showQrCode = ctx.showQrCode !== false
      this.deferUpstreamPay = !!ctx.deferUpstreamPay
      this.douyinzfChannel = !!ctx.douyinzfChannel
      if (this.deferUpstreamPay) {
        this.showQrCode = false
      }
      // 抖音直付手机：不展码（PC 仍展示）
      if (this.douyinzfMobileDeferOutcode) {
        this.showQrCode = false
      }
      this.restoreDouyinzfAndroidQr()
      this.qrAlipayImmediateLaunchMode = ctx.qrAlipayImmediateLaunchMode
        ? String(ctx.qrAlipayImmediateLaunchMode)
        : 'yuansheng'
      this.expireAtMillis = ctx.expireAtMillis != null ? Number(ctx.expireAtMillis) : null
      this.amountYuan = ctx.amountYuan || '0.00'
      this.mchOrderNo = ctx.mchOrderNo || ''
      this.createTime = ctx.createTime || ''
      this.payType = ctx.payType ? String(ctx.payType) : ''
      this.cardDim = this.expired
      if (ctx.orderId) {
        this.orderId = String(ctx.orderId)
      }
      if (ctx.token) {
        this.token = String(ctx.token)
      }
      this.buyerOAuthRequired = !!ctx.buyerOAuthRequired
      this.buyerOAuthOpenUrl = ctx.buyerOAuthOpenUrl ? String(ctx.buyerOAuthOpenUrl) : ''
      this.buyerOAuthInAppLaunchUrl = ctx.buyerOAuthInAppLaunchUrl ? String(ctx.buyerOAuthInAppLaunchUrl) : ''
      this.buyerOAuthExternalLaunchScheme = ctx.buyerOAuthExternalLaunchScheme
        ? String(ctx.buyerOAuthExternalLaunchScheme)
        : ''
      this.buyerOAuthPullIsvAppId = ctx.buyerOAuthPullIsvAppId ? String(ctx.buyerOAuthPullIsvAppId) : ''
      this.buyerOAuthProcessUrl = ctx.buyerOAuthProcessUrl ? String(ctx.buyerOAuthProcessUrl) : ''
      this.buyerOAuthStateConsumed = !!ctx.buyerOAuthStateConsumed
      this.orderPaying = !!ctx.orderPaying
      this.channelId = ctx.channelId != null && ctx.channelId !== '' ? ctx.channelId : null
      this.channelName = ctx.channelName ? String(ctx.channelName) : ''
      this.hldChannel = !!ctx.hldChannel
      this.hldResumeWarmUrl = ctx.hldResumeWarmUrl ? String(ctx.hldResumeWarmUrl) : ''
      this.jsapiRequired = !!ctx.jsapiRequired
      this.jsapiMpLaunchScheme = ctx.jsapiMpLaunchScheme ? String(ctx.jsapiMpLaunchScheme) : ''
      this.jsapiMpLaunchRenderUrl = ctx.jsapiMpLaunchRenderUrl ? String(ctx.jsapiMpLaunchRenderUrl) : ''
      this.jsPayMode = ctx.jsPayMode ? String(ctx.jsPayMode) : 'TRADE_PAY'
      this.cashierPresentation = ctx.cashierPresentation ? String(ctx.cashierPresentation) : 'STANDARD'
      this.copyOpenHint = ctx.copyOpenHint ? String(ctx.copyOpenHint) : DEFAULT_COPY_OPEN_HINT
      this.payerNameFilled = !!ctx.payerNameFilled
      this.startExpireCountdown()
    },
    onConfirmPayerName () {
      const name = String(this.payerNameInput || '').trim()
      if (!name) {
        this.payerNameError = '请输入付款人姓名'
        return
      }
      if (this.payerNameSubmitting) {
        return
      }
      this.payerNameError = ''
      this.payerNameSubmitting = true
      submitUnifiedPayerName(this.orderId, this.token, name)
        .then(() => {
          this.payerNameFilled = true
          this.payerNameSubmitting = false
          if (this.deferUpstreamPay) {
            this.btnPayDisabled = false
            return
          }
          this.pageBootHint = '正在连接支付渠道…'
          return this.bootPreload().then(() => {
            this.btnPayDisabled = false
          })
        })
        .catch((err) => {
          this.payerNameSubmitting = false
          const msg = (err && (err.message || err.msg)) ? String(err.message || err.msg) : '提交失败，请重试'
          this.payerNameError = msg
        })
    },
    onCopyPayLink () {
      if (this.expired) {
        return
      }
      const url = this.copyOpenPayUrl || resolveCopyOpenUrl(this.latestPayload)
      copyTextToClipboard(url)
        .then(() => {
          this.copyToast = '链接已复制'
          setTimeout(() => { this.copyToast = '' }, 2500)
        })
        .catch((e) => {
          this.copyToast = (e && e.message) ? e.message : '复制失败'
        })
    },
    /** 首屏缓冲：仅一次 execute，二维码就绪后再展示主界面（不调服务端预拉单） */
    bootPreload () {
      if (this.invalidToken || this.expired) {
        return Promise.resolve()
      }
      if (this.douyinzfChannel) {
        const loading = this.douyinzfOutcodeLoading
        this.pageBootTitle = loading.title
        this.pageBootSub = loading.sub
        this.pageBootHint = loading.hint
      } else {
        this.pageBootHint = '正在连接支付渠道…'
      }
      const promise = requestExecutePayload(this.orderId, this.token)
        .then((d) => {
          this.latestPayload = d
          if (d && d.payType) {
            this.payType = String(d.payType)
          }
          if (d && d.expireAtMillis != null) {
            this.expireAtMillis = Number(d.expireAtMillis)
            this.startExpireCountdown()
          }
          if (d && d.cashierPresentation) {
            this.cashierPresentation = String(d.cashierPresentation)
          }
          if (d && d.copyOpenHint) {
            this.copyOpenHint = String(d.copyOpenHint)
          }
          if (isExecutePayloadReady(d)) {
            this.btnPayDisabled = false
          }
          if (isUnsupportedSinopecUnionPay(d)) {
            throw new Error(UNSUPPORTED_SINOPEC_UNION_PAY_MSG)
          }
          if (this.isCopyOpenMode) {
            this.pageBootHint = '正在获取支付链接…'
            this.copyOpenPayUrl = resolveCopyOpenUrl(d)
            return d
          }
          if (!this.showQrCode || this.douyinzfMobileDeferOutcode) {
            return d
          }
          this.pageBootHint = '正在生成支付二维码…'
          return this.renderQrWithFallback(d).then((ok) => {
            if (!ok && this.isCcbPayPresentation) {
              this.qrPlaceholderText = '龙支付链接生成中，请稍后刷新页面'
              return d
            }
            if (!ok) {
              throw new Error(this.qrPlaceholderText || '二维码加载失败')
            }
            return d
          })
        })
      this.preloadPromise = promise
      return promise
    },
    handlePreloadError (err) {
      const msg = (err && err.message) ? String(err.message) : ''
      if (msg.indexOf('过期') !== -1 || msg.indexOf('失效') !== -1) {
        this.markExpiredLocal()
      }
      if (this.isCopyOpenMode && !this.invalidToken) {
        this.copyOpenPayUrl = ''
        if (!this.expired) {
          this.copyToast = (msg || '支付链接获取失败，请刷新重试')
        }
      } else if (this.showQrArea) {
        this.qrPlaceholderText = this.isCcbPayPresentation
          ? (msg || '二维码加载失败，请刷新页面重试')
          : (msg || '二维码加载失败，请点「立即支付」')
      } else {
        this.errVisible = true
        this.errText = msg || '支付数据加载失败'
      }
    },
    initPage () {
      if (!this.orderId || !this.token) {
        this.finishPageBoot(true)
        return
      }
      fetchUnifiedCashierPage(this.orderId, this.token)
        .then((ctx) => {
          this.applyPageContext(ctx)
          if (this.invalidToken || this.expired) {
            this.finishPageBoot(false)
            return
          }
          if (this.preferInAppOAuthPay || this.preferJsapiPay || this.hldResumeAvailable) {
            this.btnPayDisabled = false
            if (this.hldResumeAvailable) {
              this.finishPageBoot(false)
              this.tryHldResumeAuto()
              return
            }
            if (this.showQrArea && this.preferInAppOAuthPay) {
              return this.renderQrWithFallback(null)
                .then(() => {
                  this.finishPageBoot(false)
                  this.tryAutopayFromQuery()
                })
                .catch(() => {
                  this.finishPageBoot(false)
                  this.tryAutopayFromQuery()
                })
            }
            this.finishPageBoot(false)
            this.tryAutopayFromQuery()
            return
          }
          if (this.isPayerNameMode && !this.payerNameFilled) {
            this.finishPageBoot(false)
            return
          }
          // LKLMP 等：进页不打上游，点「立即支付」再出链（配合长 loading）
          if (this.deferUpstreamPay) {
            this.btnPayDisabled = false
            this.finishPageBoot(false)
            return
          }
          // 抖音直付手机：进页不出链，点「立即支付」再请求插件
          if (this.douyinzfMobileDeferOutcode) {
            this.btnPayDisabled = false
            this.finishPageBoot(false)
            return
          }
          return this.bootPreload()
            .then(() => {
              this.finishPageBoot(false)
            })
            .catch((err) => {
              this.handlePreloadError(err)
              this.finishPageBoot(false)
            })
        })
        .catch(() => {
          this.pageBooting = false
          this.invalidToken = false
          this.expired = false
          this.amountYuan = '--'
          this.errVisible = true
          this.errText = '网络异常，请刷新重试'
        })
    },
    onPayClick () {
      if (this.invalidToken) {
        return
      }
      if (this.expired) {
        redirectCashierExpiredPay()
        return
      }
      if (this.oauthAwaitingPayment) {
        this.showErr('请完成转账')
        return
      }

      if (this.hldResumeAvailable) {
        this.openHldResumeWarm(false)
        return
      }

      this.hideErr()
      this.btnPayDisabled = true

      const dismissPayLoadingWithError = (msg) => {
        this.payLoadingCtrl.hide()
        this.showErr(msg)
        this.btnPayDisabled = false
      }
      const showPayLoading = (initialHint, safetyMs) => {
        this.payLoadingCtrl.show({
          initialHint,
          safetyMs,
          onSafetyTimeout: () => dismissPayLoadingWithError('支付请求超时，请稍后重试')
        })
      }

      if (this.preferInAppOAuthPay) {
        const inAlipay = isAlipayWebView() || hasAlipayJsRuntime()
        const oauthLoading = resolveOAuthProcessLoading(this.buyerOAuthContext)
        this.payLoadingCtrl.show({
          mode: 'oauth',
          title: oauthLoading.detect.title,
          sub: inAlipay
            ? oauthLoading.detect.sub
            : '正在唤起支付…',
          initialHint: inAlipay
            ? oauthLoading.detect.sub
            : '正在唤起支付…',
          safetyMs: 90000,
          onSafetyTimeout: () => dismissPayLoadingWithError('支付请求超时，请稍后重试')
        })
        runBuyerOAuthForImmediatePay(this.buyerOAuthContext, {
          onProgress: (phase) => {
            if (this.payLoadingCtrl && phase) {
              this.payLoadingCtrl.setPhase(phase)
            }
          }
        })
          .then(() => {
            // 已 location 跳转离页；勿恢复按钮，避免卸载前二次点击
          })
          .catch((e) => {
            this.payLoadingCtrl.hide()
            this.showErr((e && e.message) ? e.message : '打开支付页失败')
            this.btnPayDisabled = false
          })
        return
      }

      if (this.preferJsapiPay) {
        showPayLoading('正在打开小程序…', 45000)
        const vm = this
        loadPayloadForPay(
          () => vm.latestPayload,
          () => vm.preloadPromise,
          vm.orderId,
          vm.token,
          true
        ).then(() => {
          return runJsapiImmediatePay(vm.jsapiContext)
        }).then(() => {
          vm.payLoadingCtrl.hide()
          vm.btnPayDisabled = false
          if (isJsapiConvertMode(vm.jsapiContext)) {
            if (vm.jsapiConvertPollTeardown) {
              vm.jsapiConvertPollTeardown()
            }
            vm.jsapiConvertPollTeardown = setupJsapiConvertPoll(vm.orderId, vm.token, function () {
              vm.payLoadingCtrl.hide()
            })
          }
        }).catch((e) => {
          vm.payLoadingCtrl.hide()
          vm.showErr((e && e.message) ? e.message : '打开小程序失败')
          vm.btnPayDisabled = false
        })
        return
      }

      const dom = this.domRefs()
      const that = this
      const cachedReady = isExecutePayloadReady(that.latestPayload)
      // deferUpstreamPay（LKL）/ 抖音直付手机：首次才打上游，用更长 safety + 「请勿关闭」
      if ((that.deferUpstreamPay || that.douyinzfChannel) && !cachedReady) {
        const loading = that.douyinzfChannel
          ? that.douyinzfOutcodeLoading
          : {
            title: '正在连接支付渠道',
            sub: '通道准备中，请勿关闭本页…',
            hint: '正在连接支付渠道…',
            safetyMs: 90000
          }
        that.payLoadingCtrl.show({
          title: loading.title,
          sub: loading.sub,
          initialHint: loading.hint,
          safetyMs: loading.safetyMs,
          onSafetyTimeout: () => dismissPayLoadingWithError('支付请求超时，请稍后重试')
        })
      } else {
        showPayLoading(
          cachedReady ? '正在跳转支付…' : '正在连接支付渠道…',
          cachedReady ? 20000 : 60000
        )
      }
      const payloadPromise = cachedReady
        ? Promise.resolve(that.latestPayload)
        : loadPayloadForPay(
          () => that.latestPayload,
          () => that.preloadPromise,
          that.orderId,
          that.token,
          false
        )
      payloadPromise.then((d) => {
        if (handleExecuteExpiredPayload(d)) {
          return
        }
        that.latestPayload = d
        if (d && d.qrAlipayImmediateLaunchMode) {
          that.qrAlipayImmediateLaunchMode = String(d.qrAlipayImmediateLaunchMode)
        }
        if (isUnsupportedSinopecUnionPay(d)) {
          that.payLoadingCtrl.hide()
          that.showErr(UNSUPPORTED_SINOPEC_UNION_PAY_MSG)
          that.btnPayDisabled = false
          return
        }
        // 抖音商城 · 仅安卓：render 短协议一点击，并记下短桥二维码（返回本页即兜底）
        if (that.douyinzfChannel) {
          const qrUrl = resolveDouyinzfAndroidQrUrl(d)
          const androidLaunch = resolveDouyinzfAndroidBridgeLaunchUrl(d)
          if (androidLaunch && qrUrl) {
            that.armDouyinzfAndroidQr(qrUrl)
            openPayNavigationUrl(androidLaunch, false)
            that.payLoadingCtrl.hide()
            that.btnPayDisabled = false
            return
          }
        }
        const ok = applyImmediatePay(d, dom, that.buyerOAuthContext, that.qrAlipayImmediateLaunchMode)
        if (!ok) {
          that.payLoadingCtrl.hide()
          that.showErr((d && d.message) ? d.message : '暂无可展示的支付数据')
          that.btnPayDisabled = false
          return
        }
        if (shouldDismissPayLoadingAfterImmediatePay(d)) {
          that.payLoadingCtrl.hide()
          that.btnPayDisabled = false
        }
      }).catch((e) => {
        that.payLoadingCtrl.hide()
        that.showErr((e && e.message) ? e.message : '网络异常')
        that.btnPayDisabled = false
      })
    }
  }
}
</script>
